import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Col,
  FormControl,
  Glyphicon,
  Grid,
  InputGroup,
  Row,
} from 'react-bootstrap';
import CarService from '../../services/CarService';
import ErrorMessage from '../ErrorMessage';
import CarValidator from './CarValidator';

const CAR_NOT_FOUND_ERROR = 'Car not found';
const DUPLICATE_CAR_ERROR = 'Car already added';

const runIfEnterPressed = func => event => event.key === 'Enter' && func(event);

class PricingValidator extends PureComponent {
  constructor() {
    super();
    this.state = {
      carList: [],
      carSearch: {
        stock: '',
        vin: '',
      },
      fetching: false,
      error: null,
    };
  }

  onStockSearch = async event => {
    event.preventDefault();
    const success = await this.addCar({ stock: this.state.carSearch.stock });
    // clear user input on success
    if (success) {
      this.setState({ carSearch: { ...this.state.carSearch, stock: '' } });
    }
  };

  onStockSearchChange = event => {
    this.setState({
      carSearch: { ...this.state.carSearch, stock: event.target.value },
    });
  };

  onVinSearch = async event => {
    event.preventDefault();
    const success = await this.addCar({ vin: this.state.carSearch.vin });
    // clear user input on success
    if (success) {
      this.setState({ carSearch: { ...this.state.carSearch, vin: '' } });
    }
  };

  onVinSearchChange = event => {
    this.setState({
      carSearch: { ...this.state.carSearch, vin: event.target.value },
    });
  };

  /**
   * Adds a car to the list, or shows an error for cars not found/missing cars
   * search by either stock # or vin, if both are provided stock takes preference
   */
  addCar = async ({ vin, stock }) => {
    let vinToSearch = vin;
    // convenience function to show an error
    const handleError = error => {
      this.setState({ fetching: false, error });
    };
    // set fetching
    this.setState({ fetching: true });
    try {
      // if a stock # is provided, search by stock first to get the vin
      if (stock) {
        const carCards = await CarService.getCarCards({
          dealer: this.props.dealer.id,
          stock,
        });
        if (!carCards || carCards.length === 0) {
          handleError(CAR_NOT_FOUND_ERROR);
          return false;
        }
        // get the vin from the car
        vinToSearch = carCards[0].id;
      }
      // prevent duplicates
      if (this.state.carList.filter(car => car.id === vinToSearch).length) {
        handleError(DUPLICATE_CAR_ERROR);
        return false;
      }
      // get the car by vin
      const car = await CarService.getCar(vinToSearch);
      if (!car) {
        handleError(CAR_NOT_FOUND_ERROR);
        return false;
      }
      // add the car to the list of displayed cars
      this.setState({
        carList: [...this.state.carList, car],
        fetching: false,
        error: null,
      });
      return true;
    } catch (err) {
      // the API will return just 'Not Found' for failed vin search, so replace it with a consistent error message
      if (err === 'Not Found') {
        handleError(CAR_NOT_FOUND_ERROR);
        return false;
      }
      handleError(err);
      return false;
    }
  };

  markCarAvailable = car => {
    this.setState({ fetching: true, error: null });
    CarService.markAvailable(car?.id)
      .then(updatedCar => {
        const carList = this.state.carList.map(c =>
          c.id === updatedCar.id ? updatedCar : c,
        );
        this.setState({ carList, fetching: false });
      })
      .catch(error => {
        this.setState({ fetching: false, error });
      });
  };

  renderCarSearch = () => {
    const {
      fetching,
      error,
      carSearch: { vin, stock },
    } = this.state;

    return (
      <Row>
        {error && <ErrorMessage error={error} />}
        <Col sm={6} md={5}>
          <InputGroup className="mb-3">
            <InputGroup.Addon>Add Car By VIN:</InputGroup.Addon>
            <FormControl
              type="text"
              value={vin}
              onChange={this.onVinSearchChange}
              onKeyPress={runIfEnterPressed(this.onVinSearch)}
            />
            <InputGroup.Button>
              <Button disabled={fetching} onClick={this.onVinSearch}>
                <Glyphicon glyph="plus" />
              </Button>
            </InputGroup.Button>
          </InputGroup>
        </Col>
        <Col sm={6} md={5}>
          <InputGroup className="mb-3">
            <InputGroup.Addon>Add Car By Stock:</InputGroup.Addon>
            <FormControl
              type="text"
              value={stock}
              onChange={this.onStockSearchChange}
              onKeyPress={runIfEnterPressed(this.onStockSearch)}
            />
            <InputGroup.Button>
              <Button disabled={fetching} onClick={this.onStockSearch}>
                <Glyphicon glyph="plus" />
              </Button>
            </InputGroup.Button>
          </InputGroup>
        </Col>
      </Row>
    );
  };

  render() {
    return (
      <Grid fluid>
        {this.renderCarSearch()}
        <br />
        <br />
        {this.state.carList.map(car => (
          <CarValidator
            key={car.id}
            car={car}
            dealer={this.props.dealer}
            onMarkCarAvailable={() => this.markCarAvailable(car)}
          />
        ))}
      </Grid>
    );
  }
}

PricingValidator.propTypes = {
  dealer: PropTypes.object,
};

const mapStateToProps = ({ dealers }) => ({ dealer: dealers.dealer });

export default connect(mapStateToProps)(PricingValidator);
